/**
 * useResponsiveContainerLayout Hook
 *
 * Monitors container width using ResizeObserver and determines
 * whether containers should be laid out horizontally or vertically.
 * Used for responsive nested container layouts that reflow when space is limited.
 */

import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { CONTAINER_CONSTRAINTS } from '../constants/constraints';
import { useOscillationSafeWidth } from '../../hooks/useOscillationSafeWidth';

/**
 * Layout mode for container rows
 * - 'horizontal': Containers side-by-side (flex-row)
 * - 'vertical': Containers stacked (flex-col)
 */
export type LayoutMode = 'horizontal' | 'vertical';

/**
 * Options for the responsive layout hook
 */
export interface ResponsiveLayoutOptions {
  /** Number of containers in the row */
  containerCount: number;
  /** Gap width in pixels between containers (default: 8) */
  gapWidth?: number;
  /** Minimum width per container in pixels before stacking (default: from constraints) */
  minContainerWidth?: number;
}

/**
 * Return value from the responsive layout hook
 */
export interface ResponsiveLayoutResult {
  /** Ref to attach to the container element for measurement */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Current layout mode based on available width */
  layoutMode: LayoutMode;
  /** Current measured width of the container */
  availableWidth: number;
}

/**
 * Hook to measure container width and determine horizontal vs vertical layout
 *
 * @param options - Configuration for responsive behavior
 * @returns Object with containerRef, layoutMode, and availableWidth
 *
 * @example
 * ```tsx
 * const { containerRef, layoutMode } = useResponsiveContainerLayout({
 *   containerCount: 3,
 *   gapWidth: 4,
 * });
 *
 * return (
 *   <div ref={containerRef} className={layoutMode === 'vertical' ? 'tw-flex-col' : 'tw-flex-row'}>
 *     {children}
 *   </div>
 * );
 * ```
 */
export const useResponsiveContainerLayout = (
  options: ResponsiveLayoutOptions
): ResponsiveLayoutResult => {
  const {
    containerCount,
    gapWidth = 8,
    minContainerWidth = CONTAINER_CONSTRAINTS.MIN_CONTAINER_WIDTH_PX,
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  // Oscillation-safe: a horizontal↔vertical flip that changes our own
  // measured width must not loop (React error #185).
  const [availableWidth, updateAvailableWidth] = useOscillationSafeWidth();

  /**
   * Calculate whether horizontal layout fits based on available width
   * Formula: containerCount * minWidth + (containerCount - 1) * gapWidth <= availableWidth
   */
  const calculateLayoutMode = useCallback(
    (width: number): LayoutMode => {
      // If width is 0 (not measured yet), default to horizontal
      if (width === 0) return 'horizontal';

      // If only one container, always horizontal
      if (containerCount <= 1) return 'horizontal';

      // Calculate minimum total width needed for horizontal layout
      const totalGapWidth = (containerCount - 1) * gapWidth;
      const minTotalWidth = containerCount * minContainerWidth + totalGapWidth;

      return width >= minTotalWidth ? 'horizontal' : 'vertical';
    },
    [containerCount, gapWidth, minContainerWidth]
  );

  // Synchronous measurement before paint — catches state-driven width changes
  // immediately so layout mode is correct on the first paint frame.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (element) {
      updateAvailableWidth(element.getBoundingClientRect().width);
    }
  });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Create ResizeObserver to monitor width changes
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        updateAvailableWidth(entry.target.getBoundingClientRect().width);
      }
    });

    // Start observing
    resizeObserver.observe(element);

    // Set initial width (border-box to match ResizeObserver)
    updateAvailableWidth(element.getBoundingClientRect().width);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [updateAvailableWidth]);

  return {
    containerRef,
    layoutMode: calculateLayoutMode(availableWidth),
    availableWidth,
  };
};

export default useResponsiveContainerLayout;
